import React, { useMemo, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useApiQuery } from '../hooks/useApi';
import { apiClient } from '../utils/apiClient';
import ErrorHandlingDemo from '../components/ErrorHandlingDemo';
import WelcomeModal from '../components/WelcomeModal';
import './Dashboard.css';

interface GearItem {
  gearId: string;
  name: string;
  category: string;
  weight?: number;
  notes?: string;
}

interface Template {
  templateId: string;
  name: string;
  gearItems: Array<{
    gearId: string;
    name: string;
    category: string;
  }>;
}

interface TripGearItem {
  gearId: string;
  name: string;
  category: string;
  packed: boolean;
  packedAt?: string;
}

interface Trip {
  tripId: string;
  name: string;
  startDate: string;
  endDate: string;
  templateId?: string;
  gearItems: TripGearItem[];
  createdAt: string;
  updatedAt: string;
}

const Dashboard: React.FC = () => {
  const { user } = useAuth();

  const [showWelcome, setShowWelcome] = useState(false);
  const [isSeeding, setIsSeeding] = useState(false);

  // Stable API call functions to prevent infinite loops
  const profileApiCall = useCallback(() => apiClient.getUserProfile(), []);
  const gearApiCall = useCallback(() => apiClient.getGear(), []);
  const templatesApiCall = useCallback(() => apiClient.getTemplates(), []);
  const tripsApiCall = useCallback(() => apiClient.getTrips(), []);

  // API hooks with stable function references
  const { data: profile, loading: profileLoading } = useApiQuery<any>(
    profileApiCall,
    []
  );

  const { data: gearItems, loading: gearLoading, refetch: refetchGear } = useApiQuery<GearItem[]>(
    gearApiCall,
    []
  );

  const { data: templates, loading: templatesLoading, refetch: refetchTemplates } = useApiQuery<Template[]>(
    templatesApiCall,
    []
  );

  const { data: trips, loading: tripsLoading, refetch: refetchTrips } = useApiQuery<Trip[]>(
    tripsApiCall,
    []
  );

  // Show welcome modal when profile + data loads and conditions are met
  useEffect(() => {
    if (profileLoading || gearLoading || tripsLoading) return;
    if (!profile) return;
    if (profile.onboardingDismissed) return;
    if ((gearItems?.length ?? 0) > 0 || (trips?.length ?? 0) > 0) return;
    setShowWelcome(true);
  }, [profile, profileLoading, gearItems, gearLoading, trips, tripsLoading]);

  const handleSeed = useCallback(async () => {
    setIsSeeding(true);
    try {
      await apiClient.seedSampleData();
      await Promise.all([refetchGear(), refetchTemplates(), refetchTrips()]);
    } finally {
      setIsSeeding(false);
      setShowWelcome(false);
    }
  }, [refetchGear, refetchTemplates, refetchTrips]);

  const handleDismiss = useCallback((dontShowAgain: boolean) => {
    setShowWelcome(false);
    if (dontShowAgain) {
      apiClient.updateUserProfile({ onboarding_dismissed: true });
    }
  }, []);

  // Calculate trip statistics
  const tripStats = useMemo(() => {
    if (!trips) return { active: 0, upcoming: 0, completed: 0, totalProgress: 0 };

    const now = new Date();
    let active = 0;
    let upcoming = 0;
    let completed = 0;
    let totalItems = 0;
    let totalPacked = 0;

    trips.forEach(trip => {
      const startDate = new Date(trip.startDate);
      const endDate = new Date(trip.endDate);
      
      if (now < startDate) {
        upcoming++;
      } else if (now > endDate) {
        completed++;
      } else {
        active++;
      }

      // Calculate overall packing progress
      totalItems += trip.gearItems.length;
      totalPacked += trip.gearItems.filter(item => item.packed).length;
    });

    const totalProgress = totalItems > 0 ? Math.round((totalPacked / totalItems) * 100) : 0;

    return { active, upcoming, completed, totalProgress };
  }, [trips]);

  // Get recent trips (last 3)
  const recentTrips = useMemo(() => {
    if (!trips) return [];
    
    return trips
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 3);
  }, [trips]);

  // Calculate individual trip progress
  const calculateTripProgress = (trip: Trip) => {
    const totalItems = trip.gearItems.length;
    const packedItems = trip.gearItems.filter(item => item.packed).length;
    return totalItems > 0 ? Math.round((packedItems / totalItems) * 100) : 0;
  };

  // Get trip status
  const getTripStatus = (trip: Trip): 'upcoming' | 'active' | 'completed' => {
    const now = new Date();
    const startDate = new Date(trip.startDate);
    const endDate = new Date(trip.endDate);
    
    if (now < startDate) return 'upcoming';
    if (now > endDate) return 'completed';
    return 'active';
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const isLoading = profileLoading || gearLoading || templatesLoading || tripsLoading;

  return (
    <>
    {showWelcome && (
      <WelcomeModal onSeed={handleSeed} onDismiss={handleDismiss} isSeeding={isSeeding} />
    )}
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>Welcome back, {user?.name || 'Traveler'}!</h1>
        <p>Ready to plan your next adventure?</p>
      </div>

      <div className="dashboard-stats">
        <div className="stat-card">
          <div className="stat-number">
            {isLoading ? '...' : gearItems?.length || 0}
          </div>
          <div className="stat-label">Gear Items</div>
          <div className="stat-icon">🎒</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">
            {isLoading ? '...' : templates?.length || 0}
          </div>
          <div className="stat-label">Templates</div>
          <div className="stat-icon">📋</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">
            {isLoading ? '...' : tripStats.active}
          </div>
          <div className="stat-label">Active Trips</div>
          <div className="stat-icon">🗺️</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">
            {isLoading ? '...' : `${tripStats.totalProgress}%`}
          </div>
          <div className="stat-label">Overall Progress</div>
          <div className="stat-icon">📊</div>
        </div>
      </div>

      <div className="dashboard-content">
        <div className="dashboard-section">
          <h2>Quick Actions</h2>
          <div className="dashboard-grid">
            <div className="dashboard-card">
              <div className="card-icon">🎒</div>
              <h3>Gear Inventory</h3>
              <p>Manage your camping and travel equipment</p>
              <Link to="/gear" className="card-button">
                View Gear
              </Link>
            </div>

            <div className="dashboard-card">
              <div className="card-icon">📋</div>
              <h3>Trip Templates</h3>
              <p>Create reusable packing lists for different trip types</p>
              <Link to="/templates" className="card-button">
                View Templates
              </Link>
            </div>

            <div className="dashboard-card">
              <div className="card-icon">🗺️</div>
              <h3>All Trips</h3>
              <p>Track your current and upcoming adventures</p>
              <Link to="/trips" className="card-button">
                View Trips
              </Link>
            </div>

            <div className="dashboard-card">
              <div className="card-icon">➕</div>
              <h3>Plan New Trip</h3>
              <p>Start planning your next adventure</p>
              <Link to="/trips?new=true" className="card-button primary">
                Plan Trip
              </Link>
            </div>
          </div>
        </div>

        {recentTrips.length > 0 && (
          <div className="dashboard-section">
            <div className="section-header">
              <h2>Recent Trips</h2>
              <Link to="/trips" className="view-all-link">View All</Link>
            </div>
            <div className="recent-trips">
              {recentTrips.map(trip => {
                const status = getTripStatus(trip);
                const progress = calculateTripProgress(trip);
                
                return (
                  <div key={trip.tripId} className={`recent-trip-card trip-${status}`}>
                    <div className="trip-header">
                      <h4 className="trip-name">{trip.name}</h4>
                      <span className={`trip-status status-${status}`}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </span>
                    </div>
                    
                    <div className="trip-details">
                      <div className="trip-dates">
                        📅 {formatDate(trip.startDate)} - {formatDate(trip.endDate)}
                      </div>
                      
                      <div className="trip-progress">
                        <div className="progress-info">
                          <span className="progress-label">Packing Progress</span>
                          <span className="progress-percentage">{progress}%</span>
                        </div>
                        <div className="progress-bar">
                          <div 
                            className="progress-fill"
                            style={{ width: `${progress}%` }}
                          ></div>
                        </div>
                        <div className="progress-stats">
                          {trip.gearItems.filter(item => item.packed).length} of {trip.gearItems.length} items packed
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {trips && trips.length === 0 && !tripsLoading && (
          <div className="dashboard-section">
            <div className="getting-started">
              <div className="getting-started-icon">🚀</div>
              <h2>Get Started</h2>
              <p>Welcome to Simple Packing! Here's how to get started:</p>
              <div className="getting-started-steps">
                <div className="step">
                  <div className="step-number">1</div>
                  <div className="step-content">
                    <h4>Add Your Gear</h4>
                    <p>Start by adding your camping and travel equipment to your inventory.</p>
                    <Link to="/gear" className="step-button">Add Gear</Link>
                  </div>
                </div>
                <div className="step">
                  <div className="step-number">2</div>
                  <div className="step-content">
                    <h4>Create Templates</h4>
                    <p>Build reusable packing lists for different types of trips.</p>
                    <Link to="/templates" className="step-button">Create Template</Link>
                  </div>
                </div>
                <div className="step">
                  <div className="step-number">3</div>
                  <div className="step-content">
                    <h4>Plan Your Trip</h4>
                    <p>Use your templates to create specific trips and track packing progress.</p>
                    <Link to="/trips" className="step-button">Plan Trip</Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error Handling Demo - Remove in production */}
        {process.env.NODE_ENV === 'development' && (
          <div className="dashboard-section">
            <ErrorHandlingDemo />
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default Dashboard;
